import { MedplumClient } from "@medplum/core";

import { EncounterProceduresScope } from "@/encounters/constants/procedures";
import {
  getActiveChargeItems,
  resolveEncounterChargeItemsLocation,
} from "@/encounters/utils/encounter-charge-items";

import { EncounterChargeItemRow } from "../types/encounter-charge-item-rows";
import { buildEncounterChargeItemRows } from "../utils/encounter-charge-item-rows";

import { searchEncounterChargeItems } from "./encounter-charge-items-search";
import { getEncounterProceduresAndConditions } from "./procedures";

type GetEncounterChargeItemRowsParams = {
  medplum: MedplumClient;
  clientId: string;
  encounterId: string;
};

export type EncounterChargeItemRowsResult = {
  rows: EncounterChargeItemRow[];
  /** The Location the encounter's charges already carry, or undefined when none do. */
  placeOfService: string | undefined;
};

/**
 * Reads the encounter's biller section: one row per captured ChargeItem, with the modifiers and
 * diagnoses of the Procedure each was captured from.
 *
 * The procedures and conditions come from the same read the capture path used
 * (`getEncounterProceduresAndConditions`), so what is displayed cannot disagree with what was
 * charged about which procedures are in scope, and it costs one search rather than one read per
 * charge.
 *
 * Errors are left to the caller — a failed read must not be mistaken for an encounter with no
 * charges, which is what an empty array here would look like.
 */
export const getEncounterChargeItemRows = async ({
  medplum,
  clientId,
  encounterId,
}: GetEncounterChargeItemRowsParams): Promise<EncounterChargeItemRowsResult> => {
  const [chargeItems, { procedures, conditions }] = await Promise.all([
    searchEncounterChargeItems(medplum, encounterId),
    // Every side of the encounter: a row's modifiers and diagnoses come from the Procedure its charge
    // was captured from, whether a provider or a biller recorded it.
    getEncounterProceduresAndConditions({
      medplum,
      clientId,
      encounterId,
      scope: EncounterProceduresScope.All,
    }),
  ]);

  return {
    rows: buildEncounterChargeItemRows({ chargeItems, procedures, conditions }),
    // A voided charge's Location must not outlive it once every active charge is gone, or the
    // Billing tab would show a Location for an encounter that no longer has one.
    placeOfService: resolveEncounterChargeItemsLocation(getActiveChargeItems(chargeItems)),
  };
};
